/**
 * <s-scroll-popup> — opens its dialog once per visitor after a scroll depth.
 *
 * Attributes:
 *   data-depth        — percentage of the page scrolled before opening (default 50)
 *   data-storage-key  — localStorage key that remembers a dismissal or redemption
 *   data-preview      — open immediately (theme editor preview), never remembered
 */
class SScrollPopup extends HTMLElement {
  #ac;
  #dialog;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const preview = this.hasAttribute('data-preview');

    if (preview) {
      requestAnimationFrame(() => this.#open());
      return;
    }
    if (this.#seen()) return;

    const depth = Math.min(Math.max(Number(this.dataset.depth) || 25, 1), 100) / 100;
    let ticking = false;
    let triggered = false;
    const check = () => {
      ticking = false;
      if (triggered) return;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      // Short pages: any real scroll counts; otherwise wait for the configured depth.
      const reached = scrollable <= 400 ? window.scrollY > 40 : window.scrollY / scrollable >= depth;
      if (!reached) return;
      if (this.#open()) {
        triggered = true;
        window.removeEventListener('scroll', onScroll);
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal: this.#ac.signal });

    this.#dialog.addEventListener('close', () => this.#remember(), opts);
    this.addEventListener('click', (e) => {
      if (e.target instanceof Element && e.target.closest('[data-scroll-popup-apply]')) this.#remember();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #open() {
    if (this.#dialog.open) return true;
    // Another dialog (e.g. cart drawer) is open: try again on the next scroll.
    if (document.querySelector("dialog[open]")) return false;
    try {
      this.#dialog.showModal();
      return true;
    } catch {
      return false;
    }
  }

  #seen() {
    try {
      return localStorage.getItem(this.dataset.storageKey) === '1';
    } catch {
      return false;
    }
  }

  #remember() {
    if (this.hasAttribute('data-preview')) return;
    try {
      localStorage.setItem(this.dataset.storageKey, '1');
    } catch {
      /* storage unavailable */
    }
  }
}

if (!customElements.get('s-scroll-popup')) customElements.define('s-scroll-popup', SScrollPopup);
